import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addLayerToActive, fetchActiveStep, addLayerAfter } from "../../../../../../redux/stepsSlice";
import { getActiveStep } from "../../../../../../redux/toolsSlice";
import { generateRandomId } from "../../../../../pages/utils/tools";
import { ELEMENTS } from "../content/Consts";
import { LargeTextLayerContentDefaults } from "../content/fields/LargeTextContent";
import { BodyTextLayerContentDefaults } from "../content/fields/BodyTextContent";
import { ImageLayerDefaults } from "../content/fields/ImageContent";
import { videoLayerDefaults } from "../content/fields/VideoContent";
import { walkthroughLayerDefaults } from "../content/fields/WalkthroughContent";
import { ButtonLayerDefaults } from "../content/fields/ButtonContent";
import { EmbedCodeLayerDefaults } from "../content/fields/EmbedCodeContent";
import { ShortAnswerLayerDefaults } from "../content/fields/ShortAnswerContent";
import { LongAnswerLayerDefaults } from "../content/fields/LongAnswerContent";
import { MultipleChoiceLayerDefaults } from "../content/fields/MultipleChoiceContent";
import { CheckboxLayerDefaults } from "../content/fields/CheckboxContent";
import { FileUploadLayerDefaults } from "../content/fields/FileUploadContent";

const t = "#028496";

// Icons mirror the approved mockup (teal outline, learner-view set).
const IconHeading = () => <svg width="17" height="17" viewBox="0 0 27 27" fill="none"><path d="M13.58 20.84h-2.11v-7.37H4.11v7.37H2V4h2.11v7.37h7.36V4h2.11v16.84ZM22 8.21v12.63h-2.11V10.53l-2.1.56V8.92l2.63-.7H22Z" fill={t} /></svg>;
const IconText = () => <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M2.5 3.33h15V5h-15V3.33ZM2.5 9.17h10v1.66h-10V9.17ZM2.5 15h15v1.67h-15V15Z" fill={t} /></svg>;
const IconImage = () => <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M4.17 9.25 5.83 7.58l4.59 4.59 2.91-2.92 2.5 2.5V4.17H4.17v5.08ZM3.33 2.5h13.34c.46 0 .83.37.83.83v13.34c0 .46-.37.83-.83.83H3.33a.83.83 0 0 1-.83-.83V3.33c0-.46.37-.83.83-.83Z" fill={t} /></svg>;
const IconVideo = () => <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><path d="M1.67 3.33c0-.22.09-.43.24-.59.16-.15.37-.24.59-.24H17.5c.46 0 .83.37.83.83v13.34c0 .46-.37.83-.83.83H2.5a.83.83 0 0 1-.83-.83V3.33Zm1.66.84v11.66h13.34V4.17H3.33Zm5.52 2.85 4.07 2.71a.3.3 0 0 1 0 .5l-4.07 2.71a.3.3 0 0 1-.46-.25V7.29a.3.3 0 0 1 .46-.25Z" fill={t} /></svg>;
const IconWalkthrough = () => <svg width="17" height="17" viewBox="0 0 22 22" fill="none"><path d="M12 8c2.4 0 4 .3 4.3 1.4C17 11 17.5 12.6 18 14c.3 1 .6 2.4.8 3.6H4c.4-1.8.9-3.7 1.4-5.6.3-1.2.6-3 2.3-3" stroke={t} strokeWidth="1.1" /><path d="M11 2c3 3 1 6 0 8" stroke={t} strokeWidth="1.1" /></svg>;
const IconButton = () => <svg width="17" height="17" viewBox="0 0 20 20" fill="none"><rect x="3" y="6.5" width="14" height="7" rx="3.5" stroke={t} strokeWidth="1.5" /></svg>;
const IconEmbed = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="m8 8-4 4 4 4M16 8l4 4-4 4M13 5l-2 14" stroke={t} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const IconShort = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 9h16M4 15h10" stroke={t} strokeWidth="2" strokeLinecap="round" /></svg>;
const IconLong = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h16" stroke={t} strokeWidth="2" strokeLinecap="round" /></svg>;
const IconChoice = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="7" cy="8" r="2.4" stroke={t} strokeWidth="1.6" /><circle cx="7" cy="16" r="2.4" stroke={t} strokeWidth="1.6" /><path d="M12 8h8M12 16h8" stroke={t} strokeWidth="1.8" strokeLinecap="round" /></svg>;
const IconCheckboxes = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="4" stroke={t} strokeWidth="1.7" /><path d="m8 12 3 3 5-6" stroke={t} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const IconFile = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M8.5 13V7m0 0 2.5 2.5M8.5 7 6 9.5M15.5 13V7m0 0 2.5 2.5M15.5 7 13 9.5" stroke={t} strokeWidth="1.4" strokeLinecap="round" /><rect x="2" y="3" width="20" height="18" rx="4" stroke={t} strokeWidth="1.4" /></svg>;

const CONTENT = [
  { type: ELEMENTS.LARGE_TEXT, name: "Heading", desc: "A large section title", Icon: IconHeading },
  { type: ELEMENTS.BODY, name: "Text", desc: "A paragraph of copy", Icon: IconText },
  { type: ELEMENTS.IMAGE, name: "Image", desc: "Upload or embed a picture", Icon: IconImage },
  { type: ELEMENTS.VIDEO, name: "Video", desc: "Vimeo, YouTube, or upload", Icon: IconVideo },
  { type: ELEMENTS.WALKTHROUGH, name: "Walkthrough", desc: "An interactive product replica", Icon: IconWalkthrough },
  { type: ELEMENTS.BUTTON, name: "Button", desc: "A call to action", Icon: IconButton },
  { type: ELEMENTS.EMBEDDED_CODE, name: "Embed code", desc: "Paste an iframe or HTML snippet", Icon: IconEmbed },
];

const FORMS = [
  { type: ELEMENTS.SHORT_ANSWER, name: "Short answer", desc: "A single-line question", Icon: IconShort },
  { type: ELEMENTS.LONG_ANSWER, name: "Long answer", desc: "A multi-line response", Icon: IconLong },
  { type: ELEMENTS.MULTIPLE_CHOICE, name: "Multiple choice", desc: "Pick one from a list", Icon: IconChoice },
  { type: ELEMENTS.CHECKBOX, name: "Checkboxes", desc: "Select any that apply", Icon: IconCheckboxes },
  { type: ELEMENTS.FILE_UPLOAD, name: "File upload", desc: "Collect a document or image", Icon: IconFile },
];

const layerDefaultFor = (type) => {
  const id = generateRandomId();
  switch (type) {
    case ELEMENTS.LARGE_TEXT: return LargeTextLayerContentDefaults(id);
    case ELEMENTS.BODY: return BodyTextLayerContentDefaults(id);
    case ELEMENTS.IMAGE: return ImageLayerDefaults(id);
    case ELEMENTS.VIDEO: return videoLayerDefaults(id);
    case ELEMENTS.WALKTHROUGH: return walkthroughLayerDefaults(id);
    case ELEMENTS.BUTTON: return ButtonLayerDefaults(id);
    case ELEMENTS.EMBEDDED_CODE: return EmbedCodeLayerDefaults(id);
    case ELEMENTS.SHORT_ANSWER: return ShortAnswerLayerDefaults(id);
    case ELEMENTS.LONG_ANSWER: return LongAnswerLayerDefaults(id);
    case ELEMENTS.MULTIPLE_CHOICE: return MultipleChoiceLayerDefaults(id);
    case ELEMENTS.CHECKBOX: return CheckboxLayerDefaults(id);
    case ELEMENTS.FILE_UPLOAD: return FileUploadLayerDefaults(id);
    default: return { id, type };
  }
};

// The element library — search + two grouped lists of row items, matching the
// approved "Add element" mockup. Clicking a row drops that element (with its
// proper defaults) onto the active step and closes the menu.
const Elements = ({ closeModal, afterLayerId, setSelectedElement }) => {
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => getActiveStep(state));
  const stepId = activeStep?.id;
  const [query, setQuery] = useState("");

  const add = (type) => {
    if (setSelectedElement) setSelectedElement(type);
    dispatch(fetchActiveStep()).then(() => {
      const newLayer = layerDefaultFor(type);
      if (afterLayerId) dispatch(addLayerAfter({ stepId, afterLayerId, newLayer }));
      else dispatch(addLayerToActive({ layer: newLayer }));
      if (closeModal) closeModal();
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (list) => (q ? list.filter((r) => r.name.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q)) : list);
    return { content: match(CONTENT), forms: match(FORMS) };
  }, [query]);

  const Row = ({ row }) => (
    <button type="button" className="un-pe-amrow" onClick={() => add(row.type)}>
      <span className="un-pe-amrow__ic"><row.Icon /></span>
      <span className="un-pe-amrow__tx">
        <span className="un-pe-amrow__nm">{row.name}</span>
        <span className="un-pe-amrow__ds">{row.desc}</span>
      </span>
    </button>
  );

  if (!stepId) return null;

  return (
    <div className="un-pe-amenu">
      <div className="un-pe-amenu__search">
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="6" stroke="#9AA6AE" strokeWidth="1.6" /><path d="m14 14 3 3" stroke="#9AA6AE" strokeWidth="1.6" strokeLinecap="round" /></svg>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search elements…"
        />
      </div>

      {filtered.content.length > 0 && (
        <>
          <div className="un-pe-amenu__gh">Content</div>
          {filtered.content.map((row) => <Row key={row.type} row={row} />)}
        </>
      )}

      {filtered.forms.length > 0 && (
        <>
          <div className="un-pe-amenu__gh">Inputs &amp; forms</div>
          {filtered.forms.map((row) => <Row key={row.type} row={row} />)}
        </>
      )}

      {filtered.content.length === 0 && filtered.forms.length === 0 && (
        <div className="un-pe-amenu__empty">No elements match “{query}”.</div>
      )}
    </div>
  );
};

export default Elements;
